<script setup>
import { computed, ref, watch } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

const props = defineProps({
  groups: { type: Array, required: true },
  inboxes: { type: Array, required: true },
  busy: { type: Boolean, default: false },
  error: { type: String, default: '' },
  mutate: { type: Function, required: true },
  refresh: { type: Function, required: true },
});
const dialog = ref(null);
const actionDialog = ref(null);
const activeId = ref(null);
const selected = ref([]);
const notice = ref('');
const pending = ref(false);
const menuOpen = ref(false);
const action = ref(null);
const name = ref('');
const target = ref('');
const candidates = ref([]);
const candidateSearch = ref('');
const localError = ref('');
const working = computed(() => pending.value || props.busy);
const ownerId = id => props.groups.find(group => group.inbox_ids.map(Number).includes(Number(id)))?.id ?? null;
const groupName = id => props.groups.find(group => group.id === id)?.name || '未分组';
const activeGroup = computed(() => props.groups.find(group => group.id === activeId.value));
const navigation = computed(() => [
  ...props.groups.map(group => ({ ...group, count: props.inboxes.filter(inbox => ownerId(inbox.id) === group.id).length })),
  { id: null, name: '未分组', count: props.inboxes.filter(inbox => ownerId(inbox.id) === null).length },
]);
const members = computed(() => props.inboxes.filter(inbox => ownerId(inbox.id) === activeId.value));
const matches = (inbox, query) => inbox.name.toLowerCase().includes(query.trim().toLowerCase());
const visible = members;
const available = computed(() => props.inboxes.filter(inbox => ownerId(inbox.id) !== action.value?.groupId));
const visibleCandidates = computed(() => available.value.filter(inbox => matches(inbox, candidateSearch.value)));
const allChecked = computed(() => visible.value.length > 0 && visible.value.every(inbox => selected.value.includes(inbox.id)));
const destinations = computed(() => navigation.value.filter(group => group.id !== action.value?.groupId));
const actionTitle = computed(() => ({ create: '新建分组', rename: '重命名分组', add: `添加收件箱到“${groupName(action.value?.groupId)}”`, move: '移动收件箱', delete: '删除分组' }[action.value?.type] || '管理分组'));
const confirmLabel = computed(() => ({ create: '创建分组', rename: '保存名称', add: `确认加入 (${candidates.value.length})`, move: '确认移动', delete: '删除分组' }[action.value?.type] || '确认'));
const invalid = computed(() => {
  if (working.value || !action.value) return true;
  if (['create', 'rename'].includes(action.value.type)) return !name.value.trim() || (action.value.type === 'rename' && name.value.trim() === groupName(action.value.groupId));
  if (action.value.type === 'add') return !candidates.value.length;
  if (action.value.type === 'move') return !action.value.ids.length || !destinations.value.some(group => String(group.id ?? '') === target.value);
  return false;
});

function choose(id) {
  if (working.value) return;
  activeId.value = id;
  selected.value = [];
  menuOpen.value = false;
}
watch(visible, inboxes => {
  selected.value = selected.value.filter(id => inboxes.some(inbox => inbox.id === id));
});
watch(() => props.groups, groups => {
  if (activeId.value !== null && !groups.some(group => group.id === activeId.value)) choose(null);
});
function toggleAll() {
  selected.value = allChecked.value ? [] : visible.value.map(inbox => inbox.id);
}
function start(type) {
  if (working.value) return;
  action.value = { type, groupId: activeId.value, ids: [...selected.value] };
  name.value = type === 'rename' ? groupName(activeId.value) : '';
  target.value = '';
  candidates.value = [];
  candidateSearch.value = '';
  localError.value = '';
  notice.value = '';
  menuOpen.value = false;
  actionDialog.value.open();
}
async function perform(method, path, data) {
  if (working.value) return false;
  pending.value = true;
  localError.value = '';
  try { return await props.mutate(method, path, data); }
  catch { localError.value = '操作失败，请重试。'; return false; }
  finally { pending.value = false; }
}
async function confirm() {
  if (invalid.value) return;
  const { type, groupId, ids } = action.value;
  // Group membership can refresh while this dialog is open. Never silently move stale selections.
  if (type !== 'create' && groupId !== null && !props.groups.some(group => group.id === groupId)) {
    localError.value = '该分组已不存在，请关闭后重新选择。'; return;
  }
  let ok = false;
  let message = '';
  if (type === 'create' || type === 'rename') {
    const value = name.value.trim();
    ok = await perform(type === 'create' ? 'post' : 'patch', type === 'create' ? '' : `/${groupId}`, { name: value });
    if (ok) {
      if (type === 'create') choose(props.groups.find(group => group.name === value)?.id ?? null);
      message = type === 'create' ? `已创建“${value}”，现在可以添加收件箱。` : '分组名称已更新。';
    }
  } else if (type === 'delete') {
    ok = await perform('delete', `/${groupId}`);
    if (ok) { choose(null); message = '分组已删除，收件箱已回到“未分组”。'; }
  } else {
    const chosen = type === 'add' ? [...candidates.value] : ids;
    const destination = type === 'add' ? groupId : (target.value ? Number(target.value) : null);
    if (chosen.some(id => !props.inboxes.some(inbox => inbox.id === id) || (type === 'move' && ownerId(id) !== groupId))) {
      localError.value = '收件箱归属已变化，请关闭后重新选择。'; return;
    }
    if (destination !== null && !props.groups.some(group => group.id === destination)) {
      localError.value = '目标分组已不存在，请重新选择。'; return;
    }
    const destinationName = groupName(destination);
    ok = await perform('post', '/move', { inbox_ids: chosen, group_id: destination });
    if (ok) { selected.value = []; message = `已将 ${chosen.length} 个收件箱${type === 'add' ? '加入' : '移动到'}“${destinationName}”。`; }
  }
  if (ok) { notice.value = message; actionDialog.value.close(); }
}
async function reorder(step) {
  if (working.value) return;
  const ids = props.groups.map(group => group.id);
  const index = ids.indexOf(activeId.value);
  if (index < 0 || index + step < 0 || index + step >= ids.length) return;
  [ids[index], ids[index + step]] = [ids[index + step], ids[index]];
  menuOpen.value = false;
  if (await perform('post', '/reorder', { ids })) notice.value = '分组顺序已更新。';
}
async function open(inboxId) {
  if (working.value) return;
  notice.value = '';
  localError.value = '';
  choose(inboxId ? ownerId(inboxId) : (props.groups[0]?.id ?? null));
  if (inboxId) selected.value = [inboxId];
  dialog.value.open();
  await props.refresh();
}
defineExpose({ open });
</script>

<template>
  <Dialog ref="dialog" title="管理频道分组" description="先选择分组，再添加或移动收件箱。每个收件箱只属于一个分组。" width="3xl" :show-confirm-button="false" :overflow-y-auto="true">
    <div v-if="error || localError" role="alert" class="text-sm text-n-ruby-11">{{ localError || error }} <button type="button" class="underline" :disabled="working" @click="refresh">重新加载</button></div>
    <p v-if="notice" role="status" class="m-0 rounded-lg bg-n-alpha-2 p-3 text-sm">{{ notice }}</p>
    <div class="group-manager-layout rounded-xl border border-n-weak overflow-hidden">
      <nav aria-label="频道分组" class="group-navigation bg-n-alpha-1 p-3">
        <Button type="button" label="新建分组" icon="i-lucide-plus" variant="faded" class="w-full mb-3" :disabled="working" @click="start('create')" />
        <div class="group-navigation-list">
          <button v-for="group in navigation" :key="group.id ?? 'ungrouped'" type="button" :aria-pressed="activeId === group.id" :disabled="working" class="group-navigation-item" :class="{ 'group-navigation-active': activeId === group.id }" @click="choose(group.id)">
            <span class="truncate">{{ group.name }}</span><span class="text-xs tabular-nums">{{ group.count }}</span>
          </button>
        </div>
      </nav>
      <section class="group-content min-w-0 flex flex-col" :aria-label="`${groupName(activeId)}中的收件箱`">
        <div class="p-4 border-b border-n-weak">
          <div class="flex items-center justify-between gap-2">
            <div class="min-w-0">
              <div class="flex items-center gap-1">
                <h4 class="m-0 font-medium truncate">{{ groupName(activeId) }}</h4>
                <Button v-if="activeGroup" type="button" icon="i-lucide-pencil" size="xs" variant="ghost" color="slate" class="shrink-0" aria-label="编辑分组名称" title="编辑分组名称" :disabled="working" @click="start('rename')" />
              </div>
              <p class="m-0 text-xs text-n-slate-11">{{ members.length }} 个收件箱</p>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <Button v-if="activeGroup" type="button" label="添加收件箱" icon="i-lucide-plus" size="sm" :disabled="working" @click="start('add')" />
              <div v-if="activeGroup" class="relative" @keydown.esc.stop="menuOpen = false">
                <Button type="button" icon="i-lucide-ellipsis" variant="ghost" aria-label="分组设置" :aria-expanded="menuOpen" :disabled="working" @click="menuOpen = !menuOpen" />
                <div v-if="menuOpen" class="group-menu" aria-label="分组设置">
                  <button type="button" :disabled="groups[0]?.id === activeId" @click="reorder(-1)">上移分组</button>
                  <button type="button" :disabled="groups[groups.length - 1]?.id === activeId" @click="reorder(1)">下移分组</button>
                  <button type="button" class="text-n-ruby-11" @click="start('delete')">删除分组</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="visible.length" class="px-4 py-2 border-b border-n-weak text-sm"><label class="flex items-center gap-2"><input type="checkbox" :checked="allChecked" :indeterminate="selected.length > 0 && !allChecked" :disabled="working" @change="toggleAll" />全选本组 <span class="text-n-slate-11">{{ visible.length }}</span></label></div>
        <div class="group-inbox-list px-4">
          <label v-for="inbox in visible" :key="inbox.id" class="group-inbox-row">
            <input v-model="selected" type="checkbox" :value="inbox.id" :disabled="working" />
            <ChannelIcon :inbox="inbox" class="size-4 shrink-0" />
            <span class="min-w-0 break-words">{{ inbox.name }}</span>
          </label>
          <div v-if="!visible.length" class="py-12 px-2 text-center text-sm text-n-slate-11">
            <template v-if="activeGroup">此分组还没有收件箱。<br />点击上方“添加收件箱”开始整理。</template>
            <template v-else>所有收件箱都已分组。</template>
          </div>
        </div>
        <div class="group-selection-bar border-t border-n-weak p-3 flex items-center justify-between gap-2 text-sm">
          <span v-if="selected.length">已选 {{ selected.length }} 个 <button type="button" class="text-n-blue-11 ml-2" :disabled="working" @click="selected = []">取消选择</button></span>
          <span v-else class="text-n-slate-11">勾选收件箱可批量移动</span>
          <Button v-if="selected.length" type="button" label="移动到…" size="sm" :disabled="working || navigation.length < 2" @click="start('move')" />
        </div>
      </section>
    </div>
    <template #footer><div class="flex justify-end"><Button type="button" label="完成" variant="faded" color="slate" :disabled="working" @click="dialog.close()" /></div></template>
  </Dialog>
  <Dialog ref="actionDialog" :title="actionTitle" :confirm-button-label="confirmLabel" cancel-button-label="取消" :disable-confirm-button="invalid" :is-loading="working" :type="action?.type === 'delete' ? 'alert' : 'edit'" :overflow-y-auto="true" @confirm="confirm">
    <div v-if="error || localError" role="alert" class="text-sm text-n-ruby-11">{{ localError || error }}</div>
    <template v-if="action?.type === 'create' || action?.type === 'rename'">
      <Input v-model="name" label="分组名称" autofocus maxlength="64" placeholder="例如：Circlio、Rinoxar、售后" :disabled="working" />
    </template>
    <template v-else-if="action?.type === 'add'">
      <p class="m-0 text-sm text-n-slate-11">勾选要加入的收件箱；已在其他分组中的收件箱会从原分组移出。</p>
      <Input v-model="candidateSearch" aria-label="搜索可添加的收件箱" placeholder="搜索收件箱" :disabled="working" />
      <div class="group-candidates">
        <label v-for="inbox in visibleCandidates" :key="inbox.id" class="group-inbox-row">
          <input v-model="candidates" type="checkbox" :value="inbox.id" :disabled="working" />
          <ChannelIcon :inbox="inbox" class="size-4 shrink-0" />
          <span class="min-w-0 flex-1 break-words">{{ inbox.name }}<span class="block text-xs text-n-slate-11">当前：{{ groupName(ownerId(inbox.id)) }}</span></span>
        </label>
        <p v-if="!visibleCandidates.length" class="py-6 text-center text-sm text-n-slate-11">{{ available.length ? '没有匹配的收件箱。' : '所有收件箱都已在此分组中。' }}</p>
      </div>
      <p role="status" class="m-0 text-sm">已选择 {{ candidates.length }} 个，将加入“{{ groupName(action.groupId) }}”。</p>
    </template>
    <template v-else-if="action?.type === 'move'">
      <p class="m-0 text-sm">将 {{ action.ids.length }} 个收件箱从“{{ groupName(action.groupId) }}”移动到：</p>
      <label class="text-sm space-y-2">目标分组<select v-model="target" class="group-input" :disabled="working"><option disabled value="" v-if="action.groupId === null">请选择分组</option><option v-for="group in destinations" :key="group.id ?? 'ungrouped'" :value="String(group.id ?? '')">{{ group.name }}</option></select></label>
      <p class="m-0 text-xs text-n-slate-11">移动后将更新分组归属，会话和消息保留。</p>
    </template>
    <p v-else-if="action?.type === 'delete'" class="m-0 text-sm">删除“{{ groupName(action.groupId) }}”后，其中的收件箱将回到“未分组”。收件箱、会话和消息不会删除。</p>
  </Dialog>
</template>

<style scoped>
.group-manager-layout { display: grid; grid-template-columns: 180px minmax(0, 1fr); height: min(52vh, 460px); min-height: 280px; }
.group-navigation { display: flex; flex-direction: column; min-height: 0; border-right: 1px solid; @apply border-n-weak; }
.group-navigation-list { overflow-y: auto; min-height: 0; }
.group-navigation-item { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; padding: 10px; border-radius: 8px; text-align: left; font-size: 14px; }
.group-navigation-item:hover { @apply bg-n-alpha-2; }
.group-navigation-active { @apply bg-n-blue-3 text-n-blue-11; }
.group-content { min-height: 0; }
.group-inbox-list { flex: 1; min-height: 0; overflow-y: auto; }
.group-inbox-row { display: flex; align-items: center; gap: 10px; padding: 14px 0; margin: 0; font-size: 14px; cursor: pointer; border-bottom: 1px solid; @apply border-n-weak; }
.group-inbox-row input { flex-shrink: 0; }
.group-input { display: block; width: 100%; min-width: 0; margin: 0; border-radius: 8px; font-size: 14px; }
.group-menu { position: absolute; right: 0; top: 100%; z-index: 10; width: 140px; padding: 4px; border: 1px solid; border-radius: 8px; @apply bg-n-background border-n-weak shadow-lg; }
.group-menu button { display: block; width: 100%; padding: 9px 12px; text-align: left; font-size: 14px; border-radius: 5px; }
.group-menu button:hover { @apply bg-n-alpha-2; }
button:disabled { opacity: .5; cursor: not-allowed; }
.group-candidates { max-height: 36vh; overflow-y: auto; }
@media (max-width: 600px) {
  .group-manager-layout { grid-template-columns: 1fr; height: min(62vh, 560px); }
  .group-navigation { max-height: 145px; border-right: 0; border-bottom: 1px solid; @apply border-n-weak; }
  .group-navigation-list { display: flex; overflow-x: auto; }
  .group-navigation-item { flex-shrink: 0; width: auto; max-width: 160px; }
}
</style>
